import { get } from "lodash";
import {
  add,
  addHours,
  differenceInHours,
  endOfHour,
  isBefore,
  startOfHour,
  toDate,
} from "date-fns";
import * as duration from "duration-fns";

import { Serie } from "@/types/serie";
import TimeRange from "@/types/time-range";

import { PlottedTimeSerie } from "./types";
export const TIME_INTERVAL = 60;

/**
 * Creates an array of dates between the start-date and end-date with steps of the time interval
 */
export const createDateTimeKeys = (timeRange: TimeRange) => {
  const startHour = startOfHour(timeRange.timeStart);
  const numHours = differenceInHours(addHours(timeRange.timeEnd, 1), startHour);

  return Array.from({ length: numHours }, (_, i) =>
    toDate(startOfHour(addHours(startHour, i))),
  );
};

export const transformSerieData = (serie: Serie): [Date, number][] => {
  return Object.entries(serie.data).map(([dateStr, value]) => {
    return [new Date(dateStr), value];
  });
};

/**
 * A value in the chart can span multiple hours. How many is defined by the serie.timeResolution
 * Here we validate the points and calculate how many hours a given point spans
 */
export const plotSeriePoints = (serie: Serie, timeRange: TimeRange) => {
  const timeInterval = duration.parse(serie.timeResolution);
  const data = transformSerieData(serie);

  const serieData: PlottedTimeSerie = {};

  data?.forEach((point) => {
    if (!isValidKeyPoint(point)) return;

    const startDate = point[0];
    const endDate = endOfHour(add(startDate, timeInterval));

    // Make the span does not go beyond the graph
    if (isBefore(endDate, timeRange.timeStart)) return;
    if (isBefore(timeRange.timeEnd, startDate)) return;

    // Calculate how many blocks this item will span
    const span = differenceInHours(endDate, startDate);

    serieData[startDate.toISOString()] = { value: point[1], span };
  });

  return serieData;
};

export const isEmpty = (value: number) => value === undefined || value === null;

const isValidKeyPoint = (value: unknown) =>
  typeof get(value, "[0].toISOString") === "function";
